/** Coquille applicative : sidebar + topbar + zone de contenu + notifications. */
import { el, clear, escapeHtml } from './dom.js';
import { icon, TYPE_META } from './icons.js';
import { toggleTheme, getTheme } from './theme.js';
import { contextMenu } from './modal.js';
import { toast } from './toast.js';
import { navigate } from '../router.js';
import { api, Drive } from '../api.js';
import { openChatSocket } from '../chat-socket.js';
import { openNode, uploadFiles } from '../apps/drive.js';
import { setupPush } from '../push.js';

const NAV = [
  { key: 'home', label: 'Accueil', icon: 'home', path: '/' },
  { key: 'drive', label: 'Mon Drive', icon: 'drive', path: '/drive' },
  { key: 'recent', label: 'Récents', icon: 'recent', path: '/recent' },
  { key: 'starred', label: 'Favoris', icon: 'star', path: '/starred' },
  { key: 'shared', label: 'Partagés avec moi', icon: 'shared', path: '/shared' },
  { key: 'trash', label: 'Corbeille', icon: 'trash', path: '/trash' },
];
const APPS = [
  { key: 'messages', label: 'Messages', icon: 'chat', path: '/messages' },
  { key: 'calendar', label: 'Agenda', icon: 'calendar', path: '/calendar' },
  { key: 'tasks', label: 'Tâches', icon: 'tasks', path: '/tasks' },
  { key: 'meet', label: 'Meet', icon: 'meet', path: '/meet' },
];

function fmtBytes(b) {
  if (!b || b < 1) return '0 o';
  const u = ['o', 'Ko', 'Mo', 'Go', 'To'];
  const i = Math.min(u.length - 1, Math.floor(Math.log(b) / Math.log(1024)));
  const v = b / Math.pow(1024, i);
  return (v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)) + ' ' + u[i];
}

export class Shell {
  constructor(user) {
    this.user = user;
    this.cleanup = null;
    this.navBadges = {};
    this.unreadByConv = new Map();
    this.sharedCount = 0;
    this.activeConv = null;
    this.build();
    this.initNotifications();
  }

  initials() {
    const u = this.user;
    return ((u.given_name || u.nickname || '?')[0] + (u.family_name || '')[0] || '').toUpperCase() || 'JHN';
  }

  build() {
    const u = this.user;
    this.sidebar = el('aside', { class: 'sidebar' });
    this.sidebar.append(
      el('div', { class: 'brand' }, el('img', { src: '/assets/logo.svg', alt: 'JHN' }), el('div', {}, el('b', {}, 'JHN'), el('br'), el('span', {}, 'Suite'))),
      this.newButton(),
      this.navList(NAV),
      el('div', { class: 'sb-section' }, 'Applications'),
      this.navList(APPS),
      ...(u.isAdmin ? [el('div', { class: 'sb-section' }, 'Système'), this.navList([{ key: 'admin', label: 'Admin', icon: 'settings', path: '/admin' }])] : []),
      el('div', { class: 'sb-spacer' }),
      this.storageBlock()
    );

    // Topbar
    this.crumbs = el('div', { class: 'crumbs' });
    this.searchInput = el('input', { type: 'search', placeholder: 'Rechercher dans JHN Suite…', 'aria-label': 'Rechercher', autocomplete: 'off' });
    this.searchResults = el('div', { class: 'search-results', style: { display: 'none' } });
    this.searchWrap = el('div', { class: 'search' }, el('span', { class: 'search-ic', html: icon('search') }), this.searchInput, this.searchResults);
    this.wireSearch();

    const menuToggle = el('button', { class: 'btn btn-icon btn-ghost menu-toggle', html: icon('menu'), title: 'Menu' });
    menuToggle.addEventListener('click', () => this.toggleSidebar());

    this.themeBtn = el('button', { class: 'btn btn-icon btn-ghost theme-toggle', title: 'Thème', html: icon(getTheme() === 'light' ? 'moon' : 'sun') });
    this.themeBtn.addEventListener('click', () => { const t = toggleTheme(); this.themeBtn.innerHTML = icon(t === 'light' ? 'moon' : 'sun'); });

    const avatar = el('button', { class: 'avatar', title: u.email || '', style: { cursor: 'pointer', border: 'none' } });
    if (u.picture) avatar.appendChild(el('img', { src: u.picture, alt: '' })); else avatar.textContent = this.initials();
    avatar.addEventListener('click', (e) => this.userMenu(e));

    this.topbarRight = el('div', { class: 'topbar-right', style: { display: 'flex', gap: '8px', alignItems: 'center' } }, this.themeBtn, avatar);

    this.topbar = el('header', { class: 'topbar' }, menuToggle, this.crumbs, el('div', { class: 'spacer' }), this.searchWrap, el('div', { class: 'spacer' }), this.topbarRight);

    this.content = el('main', { class: 'view' });
    this.main = el('div', { class: 'main' }, this.topbar, this.content);
    this.backdrop = el('div', { class: 'sidebar-backdrop' });
    this.backdrop.addEventListener('click', () => this.closeSidebar());
    this.root = el('div', { class: 'shell' }, this.sidebar, this.main, this.backdrop);

    const app = document.getElementById('app');
    clear(app).appendChild(this.root);
    app.removeAttribute('aria-busy');
  }

  toggleSidebar() { this.root.classList.toggle('nav-open'); this.sidebar.classList.toggle('open'); }
  closeSidebar() { this.root.classList.remove('nav-open'); this.sidebar.classList.remove('open'); }

  navList(items) {
    const list = el('div', { class: 'nav-list' });
    for (const it of items) {
      const badge = el('span', { class: 'nav-badge', style: { display: 'none' } });
      this.navBadges[it.key] = badge;
      const node = el('div', { class: 'nav-item', 'data-key': it.key, html: icon(it.icon) + `<span>${escapeHtml(it.label)}</span>` });
      node.appendChild(badge);
      node.addEventListener('click', () => { this.closeSidebar(); navigate(it.path); });
      list.appendChild(node);
    }
    return list;
  }

  newButton() {
    const btn = el('button', { class: 'btn btn-primary new-btn', html: icon('plus') + '<span>Nouveau</span>' });
    btn.addEventListener('click', () => {
      const r = btn.getBoundingClientRect();
      const parent = this.currentFolder || null;
      contextMenu(r.left, r.bottom + 6, [
        { label: 'Dossier', icon: 'folder', onClick: () => this.create('folder', parent) },
        { sep: true },
        { label: 'Document', icon: 'doc', onClick: () => this.create('doc', parent) },
        { label: 'Feuille de calcul', icon: 'sheet', onClick: () => this.create('sheet', parent) },
        { label: 'Présentation', icon: 'slides', onClick: () => this.create('slides', parent) },
        { sep: true },
        { label: 'Importer un fichier', icon: 'upload', onClick: () => this.importFiles(parent) },
      ]);
    });
    return btn;
  }

  async create(type, parent) {
    try {
      if (type === 'folder') {
        const { promptModal } = await import('./modal.js');
        const name = await promptModal({ title: 'Nouveau dossier', label: 'Nom', value: 'Nouveau dossier', confirm: 'Créer' });
        if (!name) return;
        await api.post('/api/drive/folder', { parent, name });
        toast('Dossier créé');
        window.dispatchEvent(new CustomEvent('jhn:refresh'));
      } else {
        const { node } = await api.post('/api/drive/file', { parent, type });
        window.open('/' + type + '/' + node.id, '_blank', 'noopener');
        window.dispatchEvent(new CustomEvent('jhn:refresh'));
      }
    } catch (e) { toast(e.message, 'err'); }
  }

  storageBlock() {
    this.storageBar = el('i', { style: { width: '0%' } });
    this.storageText = el('span', {}, 'Calcul de l’espace…');
    return el('div', { class: 'storage' },
      el('div', { style: { fontSize: '13px', fontWeight: 600 } }, 'Stockage'),
      el('div', { class: 'bar' }, this.storageBar),
      this.storageText);
  }
  async loadStorage() {
    try {
      const { used, quota } = await Drive.usage();
      const pct = quota ? Math.min(100, Math.max(used > 0 ? 1.5 : 0, (used / quota) * 100)) : 0;
      this.storageBar.style.width = pct.toFixed(pct < 10 ? 1 : 0) + '%';
      this.storageText.textContent = `${fmtBytes(used)} utilisés sur ${fmtBytes(quota)}`;
    } catch { this.storageText.textContent = 'Espace personnel JHN'; }
  }

  importFiles(parent) {
    const inp = el('input', { type: 'file', multiple: true, accept: '.mp4,.mp3,.pdf,.jpg,.jpeg,.png,.md,.webp,.gif,.m4a,.mov,.txt,image/*,video/*,audio/*,application/pdf', style: { display: 'none' } });
    inp.addEventListener('change', async () => { if (inp.files.length) { await uploadFiles(inp.files, parent); window.dispatchEvent(new CustomEvent('jhn:refresh')); } inp.remove(); });
    document.body.appendChild(inp); inp.click();
  }

  // ── Recherche globale (dropdown) ──
  wireSearch() {
    let timer, lastQ = '';
    const run = async () => {
      const q = this.searchInput.value.trim();
      lastQ = q;
      if (!q) return this.hideSearch();
      try {
        const { nodes } = await Drive.search(q);
        if (q !== lastQ) return;
        this.renderSearch(nodes, q);
      } catch { this.hideSearch(); }
    };
    this.searchInput.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(run, 220); });
    this.searchInput.addEventListener('focus', () => { if (this.searchResults.childElementCount) this.searchResults.style.display = ''; });
    this.searchInput.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.searchInput.value = ''; this.hideSearch(); this.searchInput.blur(); } });
    document.addEventListener('mousedown', (e) => { if (!this.searchWrap.contains(e.target)) this.hideSearch(); });
  }
  hideSearch() { this.searchResults.style.display = 'none'; }
  renderSearch(nodes, q) {
    clear(this.searchResults);
    if (!nodes.length) {
      this.searchResults.appendChild(el('div', { class: 'search-empty' }, `Aucun résultat pour « ${escapeHtml(q)} »`));
    } else {
      for (const n of nodes) {
        const meta = TYPE_META[n.type] || TYPE_META.doc;
        const row = el('div', { class: 'search-row' },
          el('span', { class: 'search-row-ic', style: { background: meta.color }, html: icon(meta.icon, 15) }),
          el('span', { class: 'search-row-nm' }, n.name),
          el('span', { class: 'search-row-ty' }, n.type === 'folder' ? 'Dossier' : meta.label));
        row.addEventListener('mousedown', (e) => { e.preventDefault(); this.searchInput.value = ''; this.hideSearch(); openNode(n, navigate); });
        this.searchResults.appendChild(row);
      }
    }
    this.searchResults.style.display = '';
  }

  setActiveNav(key) {
    this.root.querySelectorAll('.nav-item').forEach((n) => n.classList.toggle('active', n.dataset.key === key));
    if (key === 'shared' && this.sharedCount) { this.sharedCount = 0; this.setBadge('shared', 0); }
  }

  setCrumbs(parts) {
    clear(this.crumbs);
    parts.forEach((p, i) => {
      if (i) this.crumbs.appendChild(el('span', { class: 'sep', html: icon('chevron', 15) }));
      const last = i === parts.length - 1;
      const c = el('span', { class: 'crumb' + (last ? ' current' : '') }, p.label);
      if (!last && p.path) c.addEventListener('click', () => navigate(p.path));
      this.crumbs.appendChild(c);
    });
  }

  async userMenu(e) {
    const r = e.currentTarget.getBoundingClientRect();
    const u = this.user;
    contextMenu(r.right - 220, r.bottom + 8, [
      { label: `${u.given_name || ''} ${u.family_name || ''}`.trim() || u.nickname || 'Mon compte', icon: 'users' },
      { sep: true },
      { label: 'Se déconnecter', icon: 'logout', danger: true, onClick: async () => { await api.post('/auth/logout'); location.href = '/'; } },
    ]);
  }

  // ── Notifications temps réel (badge messages + partages) ──
  initNotifications() {
    this.chat = openChatSocket();
    this.chat.on('open', () => this.loadUnread());
    this.chat.on('message', ({ message }) => {
      if (message.sender_id === this.user.id) return;
      if (this.activeConv === message.conversation_id && document.visibilityState === 'visible') return;
      this.unreadByConv.set(message.conversation_id, (this.unreadByConv.get(message.conversation_id) || 0) + 1);
      this.refreshMsgBadge();
      this.notify('Nouveau message', message.type === 'photo' ? (message.ephemeral ? '📸 Photo éphémère' : '🖼️ Photo') : message.body, '/messages', message.conversation_id);
    });
    this.chat.on('shared', (d) => {
      this.sharedCount++; this.setBadge('shared', this.sharedCount);
      toast(`${d.by} a partagé « ${d.node.name} »`);
      this.notify('Nouveau partage', `${d.by} a partagé « ${d.node.name} »`, '/shared', 'share');
    });
    this.loadUnread();
    this.loadStorage();
    window.addEventListener('jhn:refresh', () => this.loadStorage());
    setupPush(); // s'abonne au push si déjà autorisé
  }
  getChat() { return this.chat; }
  setActiveConversation(id) {
    this.activeConv = id;
    if (id && this.unreadByConv.has(id)) { this.unreadByConv.delete(id); this.refreshMsgBadge(); }
  }
  async loadUnread() {
    try {
      const { conversations } = await api.get('/api/chat/conversations');
      this.unreadByConv.clear();
      // La conversation actuellement ouverte est considérée comme lue (pas de badge).
      for (const c of conversations) if (c.unread && c.id !== this.activeConv) this.unreadByConv.set(c.id, c.unread);
      this.refreshMsgBadge();
    } catch {}
  }
  refreshMsgBadge() {
    let total = 0; this.unreadByConv.forEach((v) => (total += v));
    this.setBadge('messages', total);
    try { if (total > 0) navigator.setAppBadge?.(total); else navigator.clearAppBadge?.(); } catch {}
  }
  setBadge(key, n) {
    const b = this.navBadges[key]; if (!b) return;
    b.textContent = n > 99 ? '99+' : String(n);
    b.style.display = n > 0 ? '' : 'none';
  }
  // Les notifications système (bannière + Centre de notifications + pastille) sont
  // gérées par le service worker via Web Push (fonctionne même app fermée). On évite
  // ici de créer une 2e notification côté page (doublon).
  notify() { /* no-op : voir public/sw.js (push) */ }

  render(view, params, navKey) {
    if (this.cleanup) { try { this.cleanup(); } catch {} this.cleanup = null; }
    this.onSearch = null;
    this.currentFolder = null;
    this.hideSearch();
    // La recherche n'a pas de sens dans la messagerie -> on la masque.
    this.searchWrap.style.display = navKey === 'messages' ? 'none' : '';
    clear(this.content);
    clear(this.crumbs);
    this.setActiveNav(navKey);
    this.content.classList.remove('fade-in'); void this.content.offsetWidth; this.content.classList.add('fade-in');
    const ctx = {
      content: this.content, shell: this, user: this.user, params, navigate,
      setCrumbs: (p) => this.setCrumbs(p), setSearch: (fn) => { this.onSearch = fn; },
    };
    this.cleanup = view(ctx) || null;
  }
}
